"use client";
import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

export type FeedType = "For You" | "Highlight";

interface FeedFilterProps {
  selectedFeed: FeedType;
  onFeedChange: (feed: FeedType) => void;
  className?: string;
}

const FeedFilter: React.FC<FeedFilterProps> = ({
  selectedFeed,
  onFeedChange,
  className = ""
}) => {
  const [showPopup, setShowPopup] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        setShowPopup(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSelectForYou = () => {
    onFeedChange("For You");
    setShowPopup(false);
  };

  const handleSelectHighlight = () => {
    onFeedChange("Highlight");
    setShowPopup(false);
  };

  return (
    <div className={`relative inline-block ${className}`}>
      {/* Feed Filter Button - styled like tab trigger */}
      <div className="flex items-center gap-2 mb-6">
        <button
          onClick={() => setShowPopup((prev) => !prev)}
          className="space-mono-text flex items-center gap-2 px-6 py-2 rounded-full border border-[#E5E7EB] bg-[#F6F2EC] text-[#6c2bff] text-sm sm:text-base transition-colors duration-200"
        >
          <span>{selectedFeed}</span>
          <ChevronDown
            className={`w-4 h-4 flex-shrink-0 transition-transform duration-200 ${
              showPopup ? "rotate-180" : ""
            }`}
          />
        </button>
      </div>

      {/* Dropdown - clean, borderless, matches tab style */}
      {showPopup && (
        <div
          ref={popupRef}
          className="absolute z-50 top-full left-0 mt-1 min-w-[180px] rounded-2xl border border-[#E5E7EB] bg-white shadow-md py-1.5 animate-in fade-in zoom-in-95 duration-200"
          style={{ boxShadow: '0 4px 20px rgba(66, 30, 122, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)' }}
        >
          {/* for you option */}
          <button
            onClick={handleSelectForYou}
            className={`space-mono-text w-full flex items-center gap-3 px-4 py-3 text-left text-sm transition-colors duration-150 ${
              selectedFeed === "For You"
                ? "bg-[#F6F2EC] text-[#6c2bff]"
                : "text-gray-700 hover:bg-[#F6F2EC] hover:text-[#6c2bff]"
            }`}
          >
            For You
          </button>

          {/* Highlights Option */}
          <button
            onClick={handleSelectHighlight}
            className={`space-mono-text w-full flex items-center gap-3 px-4 py-3 text-left text-sm transition-colors duration-150 ${
              selectedFeed === "Highlight"
                ? "bg-[#F6F2EC] text-[#6c2bff]"
                : "text-gray-700 hover:bg-[#F6F2EC] hover:text-[#6c2bff]"
            }`}
          >
            Highlights
          </button>
        </div>
      )}
    </div>
  );
};

export default FeedFilter;
